// Client-side CSV export helper.

export interface CsvColumn<T> {
	header: string;
	accessor: (row: T) => string | number | null | undefined;
}

// Characters that Excel / LibreOffice Calc / Google Sheets treat as the
// start of a formula. A cell beginning with any of them is evaluated as
// code on open. Tracking event properties are user-supplied (mobile
// clients can write any string), and admin CSV exports are a prime
// exfiltration target — prefix with an apostrophe to neutralise. This
// is the OWASP-recommended mitigation; matches papaparse's
// `escapeFormulae` option.
//
// Leading whitespace counts: spreadsheet apps still treat e.g. " =1+1"
// as a formula because they trim leading spaces before parsing. We
// allow zero or more leading space / tab characters before the trigger
// so a payload prefixed with whitespace can't bypass the guard.
const FORMULA_TRIGGERS = /^[ \t]*[=+\-@\t\r]/;

const escapeCell = (raw: string | number | null | undefined): string => {
	if (raw === null || raw === undefined) return "";
	let s = String(raw);
	if (FORMULA_TRIGGERS.test(s)) {
		s = `'${s}`;
	}
	if (/[",\n\r]/.test(s)) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
};

export const buildCsv = <T>(
	rows: readonly T[],
	columns: readonly CsvColumn<T>[]
): string => {
	const lines: string[] = [];
	lines.push(columns.map((c) => escapeCell(c.header)).join(","));
	for (const row of rows) {
		lines.push(columns.map((c) => escapeCell(c.accessor(row))).join(","));
	}
	return lines.join("\n");
};

export const downloadCsv = <T>(
	filename: string,
	rows: readonly T[],
	columns: readonly CsvColumn<T>[]
): void => {
	const csv = buildCsv(rows, columns);
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

export const slugForFilename = (s: string): string =>
	s
		.replace(/[^a-z0-9-]+/gi, "-")
		.replace(/^-+|-+$/g, "")
		.toLowerCase();
